import {useMutation} from '@tanstack/react-query';
import React, {FC, ReactNode, useEffect, useMemo, useState} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {usePrevious} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {UnreachableError} from '../../../../../libs/shared/core/src/helpers/types/customErrors';
import {InboxModel} from '../../../../../libs/shared/core/src/models/api/inboxModel';
import {TeamModel} from '../../../../../libs/shared/core/src/models/api/teamModel';
import {DataEventActionsEnum} from '../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {DataEventObjectName} from '../../../../../libs/shared/core/src/models/tracking/dataEventObjectName';
import {OnboardingDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/onboardingDataEventObject';
import {postApiAsync} from '../../../../../libs/shared/core/src/services/http/http';
import {useDataEventTracking} from '../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {AppPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {AsyncActionButton} from '../../../../../libs/shared/ui/src/components/buttons/loadingButton/asyncActionButton';
import {OkCancelDialog} from '../../../../../libs/shared/ui/src/components/dialogs/okCancelDialog';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {CircleModel} from '../../../../models/api/circleModel';
import {ApiAdminOnboardingResources} from '../../../../models/api/onboardingModels';
import {DepartmentsEnum} from '../../../../models/api/userMetadataModel';
import {
  SettingsInboxesResourcePagesEnum,
  SettingsInboxesTabPagesEnum,
  SettingsSingleInboxSubPagesEnum,
} from '../../../../models/url/models/settingsInboxesUrlModel';
import {SettingsResourcePagesEnum, SettingsRootPagesEnum} from '../../../../models/url/models/types';
import {buildSettingsPath} from '../../../../models/url/settingsUrlHelpers';
import {SettingsCirclesEditorTabsEnum} from '../../../../models/url/settingsUrlModel';
import {useInvalidateWorkflows} from '../../../../settings/fetchers/workflows';
import {useInboxOnCreate} from '../../../../settings/inboxes/newCreator/settingsInboxesNewCreator';
import {useSettingsInboxesNewCreatorState} from '../../../../settings/inboxes/newCreator/settingsInboxesNewCreatorState';
import {
  getActivationPreferences,
  getCompany,
  getWorkflowsTaskIsComplete,
} from '../../../../store/data/company/companySelectors';
import {dataUpdatedAction} from '../../../../store/data/dataGroupsActions';
import {updateInboxAsyncAction} from '../../../../store/data/inboxes/inboxesActions';
import {getSandboxChatInbox, getSandboxGeneralInbox} from '../../../../store/data/inboxes/inboxesSelectors';
import {getUserId} from '../../../../store/data/teammates/teammatesSelectors';
import {
  TeamBusinessHoursPreferencesUpdatedAction,
  TeamPreferencesActionsEnum,
} from '../../../../store/data/teamPreferences/teamPreferencesActions';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {getAdminSetupTeamInbox} from '../../../../store/stateless/onboardingSelectors';
import {useOnboardingModalState} from '../../../app/topBar/onboarding/onboardingContext';
import {useOnboardingFlow} from '../../flow/onboardingFlowHelpers';
import {OnboardingFlowTransitionEnum} from '../../flow/onboardingFlowTypes';
import {OnboardingGuidePopover, OnboardingGuideRenderer} from '../../guide/onboardingGuidePopover';
import {OnboardingGuidesEnum} from '../../guide/onboardingGuideTypes';
import {useSharedWorkspaceTaskIsComplete} from '../../tasks/onboardingTaskHelpers';
import {
  OnboardingAdminWizardVisual,
  OnboardingAdminWizardVisualFrame,
} from '../../welcome/onboardingAdminWizardVisual';
import {
  OnboardingInboxAccess,
  onboardingInboxAccessAll,
  OnboardingInboxSlide,
} from '../../welcome/onboardingInboxSlide';
import {OnboardingInboxVisualInboxPosition} from '../../welcome/onboardingInboxVisual';
import {OnboardingTeammateGroupsSlide} from '../../welcome/onboardingTeammateGroupsSlide';
import {generateStepText, getTeamDisplayName} from '../../welcome/onboardingWelcomeHelpers';
import {
  OnboardingWorkflowsCreatedSlide,
  WorkflowGoal,
} from '../../welcome/workflows/onboardingWorkflowsCreatedSlide';
import {StagesEnum} from '../../welcome/workflows/onboardingWorkflowsProgressSlide';

/*
 * Constants.
 */

enum AdminWizardSlidesEnum {
  INBOX = 'inbox_slide',
  TEAMMATE_GROUPS = 'teammate_group_slide',
  CREATED_RESOURCES = 'created_resources',
}

const SLIDE_TRACKING_OBJECT_NAME: Record<AdminWizardSlidesEnum, DataEventObjectName | null> = {
  [AdminWizardSlidesEnum.TEAMMATE_GROUPS]: OnboardingDataEventObject.ADMIN_WIZARD_TEAMMATE_GROUP,
  [AdminWizardSlidesEnum.INBOX]: OnboardingDataEventObject.ADMIN_WIZARD_INBOX,
  [AdminWizardSlidesEnum.CREATED_RESOURCES]: OnboardingDataEventObject.ADMIN_WIZARD_CREATED_RESOURCES,
};

const VISUAL_FRAME: Record<AdminWizardSlidesEnum, OnboardingAdminWizardVisualFrame | null> = {
  [AdminWizardSlidesEnum.TEAMMATE_GROUPS]: OnboardingAdminWizardVisualFrame.TEAMMATE_GROUPS,
  [AdminWizardSlidesEnum.INBOX]: OnboardingAdminWizardVisualFrame.INBOX,
  [AdminWizardSlidesEnum.CREATED_RESOURCES]: OnboardingAdminWizardVisualFrame.CREATED_RESOURCES,
};

const VISUAL_POSITION: Record<AdminWizardSlidesEnum, OnboardingInboxVisualInboxPosition | null> = {
  [AdminWizardSlidesEnum.TEAMMATE_GROUPS]: null,
  [AdminWizardSlidesEnum.INBOX]: OnboardingInboxVisualInboxPosition.LEFT,
  [AdminWizardSlidesEnum.CREATED_RESOURCES]: OnboardingInboxVisualInboxPosition.CENTER,
};

const TOTAL_STEP_COUNT = 2;

/*
 * Props.
 */

export interface AdminWizardPopoverProps {
  panelId: AppPanelId;
  team: TeamModel;
  onChangeDepartment: (arg: DepartmentsEnum) => void;
  onClose: () => void;
  isAdminWizardOpen: boolean;
  renderAdminWizardTransition: (render: OnboardingGuideRenderer) => ReactNode;
}

/*
 * Component.
 */

export const AdminWizardPopover: FC<AdminWizardPopoverProps> = (props) => {
  const {onChangeDepartment, onClose, team, isAdminWizardOpen, renderAdminWizardTransition} = props;

  const dispatch = useAppDispatch();
  const userId = useAppSelector(getUserId);
  const companyActivationPreferences = useAppSelector(getActivationPreferences);
  const {trackDataEvent} = useDataEventTracking();

  const {navigateOnboardingFlow} = useOnboardingFlow();

  const participatingInbox = useAppSelector(getAdminSetupTeamInbox);

  const hasCompletedWizard = useSharedWorkspaceTaskIsComplete();
  const [slide, setSlide] = useState<AdminWizardSlidesEnum>(
    computeInitialSlideState(hasCompletedWizard ?? false),
  );
  const previousSlide = usePrevious(slide);

  const visualFrame = VISUAL_FRAME[slide];
  const previousVisualPosition = previousSlide && VISUAL_POSITION[previousSlide];
  const visualPosition = VISUAL_POSITION[slide];

  // If the user has already completed the wizard, show them the resources they created.
  const shouldShowViewCreatedResources =
    (previousSlide === null || previousSlide === AdminWizardSlidesEnum.CREATED_RESOURCES) &&
    slide === AdminWizardSlidesEnum.CREATED_RESOURCES;

  // Company name state is here because we want the modified company name to be reflected in the inbox slide animation.
  const company = useAppSelector(getCompany);

  // Teammate group slide state.
  const [selectedTeamType, setSelectedTeamType] = useState<DepartmentsEnum | string>('');
  const [createdCircle, setCreatedCircle] = useState<CircleModel | undefined>();
  const [otherTeam, setOtherTeam] = useState('');

  const [hasTeammateGroupError, setHasTeammateGroupError] = useState<boolean>(false);

  const {namespace} = team;

  const isOtherTeamType = selectedTeamType === DepartmentsEnum.OTHER;

  const teamDisplayName = useMemo(
    () => (isOtherTeamType ? otherTeam : getTeamDisplayName(selectedTeamType).displayName),
    [isOtherTeamType, otherTeam, selectedTeamType],
  );
  const [initialHasTeamType] = useState(Boolean(selectedTeamType));

  // Inbox slide state.
  const {
    inboxState,
    canSave,
    onSetIsTicketingInbox,
    onSetName,
    onSetCircleIds,
    onSetIsPublic,
    onSetPinnedTeammatesIds,
  } = useSettingsInboxesNewCreatorState(namespace);

  const inboxCreationHelper = useInboxOnCreate(namespace, inboxState);

  const {createdInbox} = inboxCreationHelper;

  const invalidateWorkflows = useInvalidateWorkflows();

  const visualInbox = createdInbox ?? participatingInbox ?? {color: 'grey', name: 'Shared inbox'};

  const {setOnboardingGuide, openAdminSetupGuideSetupScreen} = useOnboardingModalState();

  const isWorkflowsGuideComplete = useAppSelector(getWorkflowsTaskIsComplete);

  const resourceMutation = useMutation({
    mutationFn: async () => {
      if (!createdInbox || !createdCircle) {
        throw new Error('Missing inbox or circle.');
      }

      const payload = {
        workflow_goals: {},
        inbox_id: createdInbox.id,
        circle_id: createdCircle.id,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        has_onboarding_prefilled_workspace_v3: true,
      };

      const createdResources = await postApiAsync(
        ApiAdminOnboardingResources,
        `${company.url}/namespaces/${namespace}/admin-onboarding/resources`,
        payload,
      );

      dispatch(dataUpdatedAction({company: createdResources.company}));

      if (createdResources.businessHours) {
        const action: TeamBusinessHoursPreferencesUpdatedAction = {
          type: TeamPreferencesActionsEnum.TEAM_BUSINESS_HOURS_PREFERENCES_UPDATED,
          payload: {
            teamId: team.id,
            businessHours: {...createdResources.businessHours, hasUpdateError: false},
          },
        };
        dispatch(action);
      }

      invalidateWorkflows();
      return createdResources;
    },
  });

  const onCircleIdChange = (circleId: number) => {
    // Set team inbox default circle to the newly created circle upon creation.
    onInboxAccessChange(circleId);
  };

  const onInboxAccessChange = (access: OnboardingInboxAccess) => {
    if (access === onboardingInboxAccessAll) {
      onSetIsPublic(true);
      onSetCircleIds([]);
    } else {
      onSetIsPublic(false);
      onSetCircleIds([access]);
    }
  };

  const onDone = async () => {
    const isOnboardingFlow = await navigateOnboardingFlow(OnboardingFlowTransitionEnum.ADMIN_WIZARD_COMPLETE);

    trackDataEvent(DataEventActionsEnum.CLICK, OnboardingDataEventObject.ADMIN_WIZARD_DONE, {
      isOnboardingFlowV5Intro: isOnboardingFlow,
    });

    if (!isOnboardingFlow) {
      onClose();

      if (!isWorkflowsGuideComplete) {
        setOnboardingGuide(OnboardingGuidesEnum.WORKFLOWS);
      } else {
        openAdminSetupGuideSetupScreen();
      }
    }
  };

  const onCancel = async (event: DataEventObjectName) => {
    const isOnboardingFlow = await navigateOnboardingFlow(OnboardingFlowTransitionEnum.ADMIN_WIZARD_CANCEL);

    trackDataEvent(DataEventActionsEnum.CLICK, event, {isOnboardingFlowV5Intro: isOnboardingFlow});

    onClose();
  };

  useEffect(() => {
    const eventName = SLIDE_TRACKING_OBJECT_NAME[slide];
    if (!isAdminWizardOpen || !eventName) {
      return;
    }
    trackDataEvent(DataEventActionsEnum.VIEW, eventName);
  }, [slide, isAdminWizardOpen, trackDataEvent]);

  const onNavigateToSharedInbox = (inboxNamespace: string, inboxId: number) => {
    trackDataEvent(
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.ADMIN_WIZARD_CREATED_RESOURCES_NAVIGATE_TO_SETTINGS_SHARED_INBOX,
    );
    dispatch(
      navigateToLinkAction(
        buildSettingsPath({
          namespace: inboxNamespace,
          inboxId,
          rootPage: SettingsRootPagesEnum.INBOXES,
          resourcePage: SettingsInboxesResourcePagesEnum.EDIT,
          subPage: SettingsSingleInboxSubPagesEnum.OVERVIEW,
          tabPage: SettingsInboxesTabPagesEnum.INBOXES,
        }),
      ),
    );
    onClose();
  };

  const onNavigateToCircle = (circleId: number) => {
    trackDataEvent(
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.ADMIN_WIZARD_CREATED_RESOURCES_NAVIGATE_TO_SETTINGS_CIRCLE,
    );
    dispatch(
      navigateToLinkAction(
        buildSettingsPath({
          circleId,
          rootPage: SettingsRootPagesEnum.CIRCLES,
          resourcePage: SettingsResourcePagesEnum.EDIT,
          tabPage: SettingsCirclesEditorTabsEnum.MEMBERS,
        }),
      ),
    );
    onClose();
  };

  const {enableTicketingInSandboxInboxes} = useEnableTicketingInSandboxInboxes();

  const renderSlide = (currentSlide: AdminWizardSlidesEnum) => {
    switch (currentSlide) {
      case AdminWizardSlidesEnum.TEAMMATE_GROUPS:
        return (
          <OnboardingTeammateGroupsSlide
            totalStepCount={TOTAL_STEP_COUNT}
            stepNumber={1}
            createdCircle={createdCircle}
            onCancel={() => onCancel(OnboardingDataEventObject.ADMIN_WIZARD_TEAMMATE_GROUP_SKIP)}
            onNext={(circle: CircleModel | undefined) => {
              setCreatedCircle(circle);
              setSlide(AdminWizardSlidesEnum.INBOX);
              trackDataEvent(
                DataEventActionsEnum.CLICK,
                OnboardingDataEventObject.ADMIN_WIZARD_TEAMMATE_GROUP_NEXT,
              );
              onSetPinnedTeammatesIds([userId]);
            }}
            selectedTeamType={selectedTeamType}
            setSelectedTeamType={setSelectedTeamType}
            setHasError={setHasTeammateGroupError}
            onChangeDepartment={onChangeDepartment}
            onCircleIdChange={onCircleIdChange}
            otherTeam={otherTeam}
            setOtherTeam={setOtherTeam}
            isOtherTeamType={isOtherTeamType}
            teamDisplayName={teamDisplayName}
            initialHasTeamType={initialHasTeamType}
          />
        );
      case AdminWizardSlidesEnum.INBOX:
        return (
          <OnboardingInboxSlide
            navSecondary={generateStepText(2, TOTAL_STEP_COUNT)}
            navPrimary={
              <ButtonGroup>
                <Button
                  variant={ButtonVariantsEnum.SECONDARY}
                  onClick={() => onCancel(OnboardingDataEventObject.ADMIN_WIZARD_INBOX_SKIP)}
                >
                  <Trans id="onboardinginbox-cancel">Set up on my own</Trans>
                </Button>
                <Button
                  variant={ButtonVariantsEnum.SECONDARY}
                  onClick={() => {
                    trackDataEvent(
                      DataEventActionsEnum.CLICK,
                      OnboardingDataEventObject.ADMIN_WIZARD_INBOX_PREVIOUS,
                    );
                    setSlide(AdminWizardSlidesEnum.TEAMMATE_GROUPS);

                    onSetIsTicketingInbox(undefined);
                  }}
                >
                  <Trans id="onboardinginbox-previous">Previous</Trans>
                </Button>

                <AsyncActionButton
                  variant={ButtonVariantsEnum.PRIMARY}
                  isDisabled={!canSave}
                  onClick={async () => {
                    trackDataEvent(
                      DataEventActionsEnum.CLICK,
                      OnboardingDataEventObject.ADMIN_WIZARD_INBOX_NEXT,
                    );
                    await inboxCreationHelper.onCreateOrEdit();

                    if (inboxState.isTicketingInbox) {
                      enableTicketingInSandboxInboxes();
                    }

                    await resourceMutation.mutateAsync();

                    setSlide(AdminWizardSlidesEnum.CREATED_RESOURCES);
                  }}
                >
                  <Trans id="onboardinginbox-next">Next</Trans>
                </AsyncActionButton>
              </ButtonGroup>
            }
            createdCircle={createdCircle}
            onSetName={onSetName}
            inboxState={inboxState}
            selectedTeamType={selectedTeamType}
            onSetIsTicketingInbox={onSetIsTicketingInbox}
          />
        );

      case AdminWizardSlidesEnum.CREATED_RESOURCES:
        return (
          <OnboardingWorkflowsCreatedSlide
            navPrimary={
              <Button variant={ButtonVariantsEnum.PRIMARY} onClick={() => onDone()}>
                <Trans id="onboardingwelcome-workflows-created-slide-done">Done</Trans>
              </Button>
            }
            adminOnboardingResources={{
              ...companyActivationPreferences.adminOnboardingResources,
              goals: [],
              circleId:
                selectedTeamType !== DepartmentsEnum.ENTIRE_COMPANY
                  ? companyActivationPreferences.adminOnboardingResources.circleId
                  : null,
            }}
            shouldShowViewCreatedResources={shouldShowViewCreatedResources}
            companyName={company.name}
            team={team}
            onNavigateToSharedInbox={onNavigateToSharedInbox}
            onNavigateToCircle={onNavigateToCircle}
            inbox={visualInbox}
            tags={resourceMutation.data?.tags ?? []}
            rules={resourceMutation.data?.rules ?? []}
          />
        );

      default:
        throw new UnreachableError(currentSlide);
    }
  };

  const hasAnyError = hasTeammateGroupError || inboxCreationHelper.hasError || resourceMutation.isError;
  if (isAdminWizardOpen && hasAnyError) {
    return (
      <OkCancelDialog
        title={<Trans id="onboardingwelcome-error-dialog-title">Some items didn&apos;t save</Trans>}
        onOkClick={() => onCancel(OnboardingDataEventObject.ADMIN_WIZARD_ERROR)}
        isCancelHidden
      >
        <Trans id="onboardingwelcome-error-dialog-tryagain">
          There were unsaved items due to an unexpected error. You&apos;ll get a chance to try again later
          from the setup guide.
        </Trans>
      </OkCancelDialog>
    );
  }

  return renderAdminWizardTransition((styles) => (
    <OnboardingGuidePopover
      onRequestClose={() => onCancel(OnboardingDataEventObject.ADMIN_WIZARD_EXIT)}
      styles={styles}
    >
      {renderSlide(slide)}
      {visualFrame !== null && (
        <OnboardingAdminWizardVisual
          frame={visualFrame}
          selectedTeamType={selectedTeamType}
          teamDisplayName={teamDisplayName}
          initialHasTeamType={initialHasTeamType}
          inboxState={inboxState}
          companyName={company.name}
          createdCircle={createdCircle}
          position={visualPosition ?? undefined}
          previousPosition={previousVisualPosition ?? undefined}
          team={team}
          slaRuleSlaReplyTimeGoal={undefined}
          tagRuleTagName=""
          tagRuleKeywords={[]}
        />
      )}
    </OnboardingGuidePopover>
  ));
};

function computeInitialSlideState(hasCompletedWizard: boolean) {
  // Shortcut to make testing easier, so we can retry the flow without creating a new company.
  if (window.location.hash === '#retry-admin-wizard') {
    return AdminWizardSlidesEnum.TEAMMATE_GROUPS;
  }

  // Start at the end, so users can see the resources they created.
  if (hasCompletedWizard) {
    return AdminWizardSlidesEnum.CREATED_RESOURCES;
  }

  // Start from the begining
  return AdminWizardSlidesEnum.TEAMMATE_GROUPS;
}

export function getProgressStages(workflowGoals: ReadonlyArray<WorkflowGoal>): Array<StagesEnum> {
  if (workflowGoals.includes(WorkflowGoal.UNDERSTAND_CONVERSATION_TRENDS)) {
    return [StagesEnum.TAGS, StagesEnum.RULES];
  }

  return [StagesEnum.RULES];
}

function useEnableTicketingInSandboxInboxes() {
  const dispatch = useAppDispatch();
  const sandboxGeneralInbox = useAppSelector(getSandboxGeneralInbox);
  const sandboxChatInbox = useAppSelector(getSandboxChatInbox);

  const enableTicketingForInbox = (inbox: InboxModel) => {
    dispatch(
      updateInboxAsyncAction({
        ...inbox,
        isTicketingEnabled: true,
        ticketIdIncrementor: {
          id: undefined,
          prefix: 'SU',
        },
      }),
    );
  };

  const enableTicketingInSandboxInboxes = () => {
    if (sandboxGeneralInbox) {
      enableTicketingForInbox(sandboxGeneralInbox);
    }

    if (sandboxChatInbox) {
      enableTicketingForInbox(sandboxChatInbox);
    }
  };

  return {enableTicketingInSandboxInboxes};
}
